import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { addMonths, eachDayOfInterval, endOfMonth, endOfWeek, format, isAfter, isBefore, isSameDay, isSameMonth, startOfDay, startOfMonth, startOfWeek, subMonths } from 'date-fns';
import { nl } from 'date-fns/locale';

@Component({
  selector: 'app-date-picker',
  imports: [CdkConnectedOverlay, CdkOverlayOrigin],
  templateUrl: './date-picker.html',
  styleUrl: './date-picker.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DatePicker {
  readonly value = input<Date | null>(null);
  readonly min = input<Date | null>(null);
  readonly max = input<Date | null>(null);
  readonly placeholder = input('dd-mm-jjjj');
  readonly ariaLabel = input('Kies datum');
  readonly valueChange = output<Date | null>();

  protected readonly open = signal(false);
  protected readonly visibleMonth = signal(startOfMonth(new Date()));
  protected readonly weekDays = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'];
  protected readonly label = computed(() => {
    const value = this.value();
    return value ? this.formatShort(value) : this.placeholder();
  });
  protected readonly monthLabel = computed(() => format(this.visibleMonth(), 'MMMM yyyy', { locale: nl }));
  protected readonly days = computed(() => {
    const month = this.visibleMonth();
    const firstDay = startOfWeek(startOfMonth(month), { weekStartsOn: 1 });
    const lastDay = endOfWeek(endOfMonth(month), { weekStartsOn: 1 });
    return eachDayOfInterval({ start: firstDay, end: lastDay });
  });

  protected toggle(): void {
    if (!this.open()) {
      this.visibleMonth.set(startOfMonth(this.value() ?? this.min() ?? new Date()));
    }

    this.open.update((value) => !value);
  }

  protected close(): void {
    this.open.set(false);
  }

  protected previousMonth(): void {
    this.visibleMonth.update((month) => subMonths(month, 1));
  }

  protected nextMonth(): void {
    this.visibleMonth.update((month) => addMonths(month, 1));
  }

  protected selectDay(day: Date): void {
    if (this.isDisabled(day)) {
      return;
    }

    this.valueChange.emit(startOfDay(day));
    this.close();
  }

  protected isOutsideMonth(day: Date): boolean {
    return !isSameMonth(day, this.visibleMonth());
  }

  protected isDisabled(day: Date): boolean {
    const normalizedDay = startOfDay(day);
    const min = this.min();
    const max = this.max();

    return (min ? isBefore(normalizedDay, startOfDay(min)) : false)
      || (max ? isAfter(normalizedDay, startOfDay(max)) : false);
  }

  protected isSelected(day: Date): boolean {
    const value = this.value();
    return value ? isSameDay(day, value) : false;
  }

  protected formatAccessible(day: Date): string {
    return format(day, 'd MMMM yyyy', { locale: nl });
  }

  private formatShort(date: Date): string {
    return format(date, 'dd-MM-yyyy', { locale: nl });
  }
}

